import { Link } from '@/app/(webapp)/_i18n/navigation';
import type { CategoryDropdownMenu } from '@/app/_shared/types/categories';
import { ContentType } from '@/graphql/graphql';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import {
    NAVIGATION_ACTIVE_ITEM_CLASS,
    NAVIGATION_ITEM_CLASS,
    NAVIGATION_LAST_ITEM_CLASS,
    NavigationDropDown,
} from './navigation-dropdown';

type Props = {
    isActive?: boolean;
    categories: CategoryDropdownMenu[];
    contentType?: ContentType;
    onLinkClickAction?: () => void;
    autoExpand?: boolean;
};

export const CategoriesDropDown = ({ isActive, categories, contentType, onLinkClickAction, autoExpand }: Props) => {
    const t = useTranslations('Navigation.buyer');
    const searchParams = useSearchParams();
    const getSelectedValues = (key: string) => {
        const rawValues = searchParams.getAll(key);
        const values = rawValues.length > 1 ? rawValues : (rawValues[0]?.split(',') ?? []);
        return values.filter(Boolean);
    };

    const currentCategoryParams = getSelectedValues('category');
    const currentGensParams = getSelectedValues('gens');
    const hasSingleActiveNavigationEntry = currentCategoryParams.length + currentGensParams.length === 1;

    const isCategoryActive = (categoryId: string, isGenre?: boolean) => {
        if (!hasSingleActiveNavigationEntry) return false;
        if (isGenre) return currentGensParams.includes(categoryId);
        return currentCategoryParams.includes(categoryId);
    };

    const basePath = contentType === ContentType.Format ? '/content-formats' : '/content-programs';

    const handleLinkClick = (close: () => void) => {
        close();
        onLinkClickAction?.();
    };
    return (
        <NavigationDropDown
            label={t(contentType === ContentType.Format ? 'formats.label' : 'programs.label')}
            isActive={isActive}
            autoOpen={autoExpand && isActive}
            panelClassName="max-h-[440px] sm:max-h-[initial] overflow-y-auto"
        >
            {(close) => (
                <>
                    <li>
                        <Link
                            className={
                                isActive && !hasSingleActiveNavigationEntry
                                    ? NAVIGATION_ACTIVE_ITEM_CLASS
                                    : `whitespace-nowrap ${NAVIGATION_ITEM_CLASS}`
                            }
                            href={basePath}
                            onClick={() => handleLinkClick(close)}
                        >
                            {t(contentType === ContentType.Format ? 'formats.all' : 'programs.all')}
                        </Link>
                    </li>
                    {categories.map((category, index, array) => {
                        return (
                            <li key={category.id}>
                                <Link
                                    className={`${isCategoryActive(category.id, category.isAnExcludedGenre) ? NAVIGATION_ACTIVE_ITEM_CLASS : `whitespace-nowrap ${NAVIGATION_ITEM_CLASS}`} ${index === array.length - 1 && array.length > 1 ? NAVIGATION_LAST_ITEM_CLASS : ''}`}
                                    href={{
                                        pathname: basePath,
                                        query: {
                                            [category.isAnExcludedGenre ? 'gens' : 'category']: category.id,
                                        },
                                    }}
                                    onClick={() => handleLinkClick(close)}
                                >
                                    {category.title as string}
                                </Link>
                            </li>
                        );
                    })}
                </>
            )}
        </NavigationDropDown>
    );
};
